import {Observable, Subscription} from 'rxjs';
import {finalize, takeUntil} from 'rxjs/operators';
import {Destroyable$} from '../ap-base-class';

// external type for TrackProgressFn
export type TrackProgressFn<TFn extends (...args: any[]) => any> = TFn & {
  readonly inProgress: boolean;
};

// this is the internal version of TrackProgressFn to allow the properties to be changed
type _TrackProgressFn<TFn extends (...args: any[]) => any> = TFn & {
  inProgress: boolean;
};

export const asyncClickHandlerVoid = onceUntilCompleteVoid;

export const asyncClickHandlerP = onceUntilCompleteP;

export const asyncClickHandler$ = onceUntilComplete$;


// Purpose: don't allow another call until the previous one is complete.
// Returns a function which synchronously return undefined if no action was taken.
export function onceUntilComplete$<TArgs extends any[], TResult>(
  fn$: (...args: [...TArgs]) => Observable<TResult>,
): TrackProgressFn<(...args: [...TArgs]) => Observable<TResult> | undefined> {
  const trackProgressFn$ = createTrackProgressFn<(...args: [...TArgs]) => Observable<TResult> | undefined>((...innerArgs) => {
    if (trackProgressFn$.inProgress) {
      return undefined;
    }
    trackProgressFn$.inProgress = true;
    return fn$(...innerArgs).pipe(
      finalize(() => {
        trackProgressFn$.inProgress = false;
      }),
    );
  });
  return trackProgressFn$;
}

// use this if the fn needs to return void because it's a click handler, or something like that
export function onceUntilCompleteVoid<TArgs extends any[], TResult>(
  destroyable: Destroyable$,
  fn$: (...args: [...TArgs]) => Observable<TResult>,
): TrackProgressFn<(...args: [...TArgs]) => void> {
  const innerFn$ = onceUntilComplete$(fn$);
  const trackProgressFn$ = createTrackProgressFn2<(...args: [...TArgs]) => void>((...innerArgs) => {
    innerFn$(...innerArgs)?.pipe(
      takeUntil(destroyable.onDestroy$),
    ).subscribe();
  }, innerFn$);
  return trackProgressFn$;
}

// use this if the fn needs to return void because it's a click handler, or something like that
// This is the same as onceUntilCompleteVoid, but without the Destroyable$
export function onceUntilCompleteVoid2<TArgs extends any[], TResult>(
  fn$: (...args: [...TArgs]) => Observable<TResult>,
): TrackProgressFn<(...args: [...TArgs]) => void> {
  const innerFn$ = onceUntilComplete$(fn$);
  const trackProgressFn$ = createTrackProgressFn2<(...args: [...TArgs]) => void>((...innerArgs) => {
    innerFn$(...innerArgs)?.subscribe();
  }, innerFn$);
  return trackProgressFn$;
}

// use this if you need to return a subscription to reference for teardown, or something like that
export function onceUntilCompleteSub<TArgs extends any[], TResult>(
  fn$: (...args: [...TArgs]) => Observable<TResult>,
  destroyable?: Destroyable$,
): TrackProgressFn<(...args: [...TArgs]) => Subscription | undefined> {
  const innerFn$ = onceUntilComplete$(fn$);
  const trackProgressFn$ = createTrackProgressFn2<(...args: [...TArgs]) => Subscription | undefined>((...innerArgs) => {
    let result$ = innerFn$(...innerArgs);
    if (destroyable && result$) {
      result$ = result$.pipe(
        takeUntil(destroyable.onDestroy$),
      );
    }
    return result$?.subscribe();
  }, innerFn$);
  return trackProgressFn$;
}

export function onceUntilCompleteP<TArgs extends any[], TResult>(
  fn: (...args: [...TArgs]) => Promise<TResult>,
): TrackProgressFn<(...args: [...TArgs]) => Promise<TResult> | undefined> {
  const trackProgressFnP = createTrackProgressFn<(...args: [...TArgs]) => Promise<TResult> | undefined>((...innerArgs) => {
    if (trackProgressFnP.inProgress) {
      return undefined;
    }
    trackProgressFnP.inProgress = true;
    return fn(...innerArgs).finally(() => {
      trackProgressFnP.inProgress = false;
    });
  });
  return trackProgressFnP;
}

export function createTrackProgressFn<TFn extends (...args: any[]) => any>(
  fn: TFn,
): _TrackProgressFn<TFn> {
  const trackProgressFn = fn as _TrackProgressFn<TFn>;
  trackProgressFn.inProgress = false;
  return trackProgressFn;
}

export function createTrackProgressFn2<TFn extends (...args: any[]) => any>(
  fn: TFn,
  parentTrackFn: TrackProgressFn<any>,
): _TrackProgressFn<TFn> {
  const trackProgressFn = fn as _TrackProgressFn<TFn>;
  Object.defineProperty(trackProgressFn, 'inProgress', {
    get() {
      return parentTrackFn.inProgress;
    },
    enumerable: true,
    configurable: false,
  });
  return trackProgressFn;
}
